<script setup>
import { getGroupUserlist } from '@/api/UserGroup';
import { addContentLogAPI } from '@/api/logServiceApi';
import useFunctions from '@/composables/useFunctions';

const useFunc = useFunctions();

const PAGE_SIZE = 10;

const props = defineProps({
  isVisible: {
    type: Boolean,
    default: false,
  },
  excludeIds: {
    type: Array,
    default: () => [],
  },
  serviceType: {
    type: String,
    default: 'harmony', // 'harmony' | 'naming'
  },
});

const emit = defineEmits(['close', 'select']);

const personList = ref([]);
const isLoading = ref(false);
const isPageLoading = ref(false);
const searchKeyword = ref('');
const currentPage = ref(1);
const totalPages = ref(0);
const listRef = ref(null);
let searchTimer = null;

const onSearchInput = (e) => {
  searchKeyword.value = e.target.value;
  clearTimeout(searchTimer);
  searchTimer = setTimeout(() => {
    fetchPersonList(1, { initial: true });
  }, 400);
};

const filteredList = computed(() => {
  const excludeSet = new Set(props.excludeIds.map(String));
  return personList.value.filter((p) => !excludeSet.has(String(p.guestUserId)));
});

const fetchPersonList = async (page = 1, { initial = false } = {}) => {
  if (initial) {
    isLoading.value = true;
  }
  isPageLoading.value = true;
  try {
    const params = {
      page,
      size: PAGE_SIZE,
      sort: 'createdAt.desc',
    };
    if (searchKeyword.value) {
      params.searchKeyWord = searchKeyword.value;
    }
    const result = await getGroupUserlist(params);
    personList.value = result?.content || [];
    currentPage.value = page;
    totalPages.value = result?.totalPages || 0;
  } catch (e) {
    personList.value = [];
    totalPages.value = 0;
  } finally {
    isLoading.value = false;
    isPageLoading.value = false;
  }
};

const goToPage = (page) => {
  if (page < 1 || page > totalPages.value || page === currentPage.value) return;
  fetchPersonList(page);
  nextTick(() => {
    listRef.value?.scrollTo({ top: 0 });
  });
};

/** 표시할 페이지 번호 목록 (최대 5개) */
const visiblePages = computed(() => {
  const total = totalPages.value;
  if (total <= 5) return Array.from({ length: total }, (_, i) => i + 1);
  let start = Math.max(1, currentPage.value - 2);
  let end = start + 4;
  if (end > total) {
    end = total;
    start = end - 4;
  }
  return Array.from({ length: end - start + 1 }, (_, i) => start + i);
});

watch(
  () => props.isVisible,
  (val) => {
    if (val) {
      document.body.style.overflow = 'hidden';
      searchKeyword.value = '';
      currentPage.value = 1;
      totalPages.value = 0;
      fetchPersonList(1, { initial: true });
    } else {
      document.body.style.overflow = '';
    }
  },
);

onBeforeUnmount(() => {
  document.body.style.overflow = '';
});


const selectPerson = (person) => {
  addContentLogAPI({
    contentType: 'ap_harmony_person_list_select_opt3',
    option3: String(person?.guestUserId ?? ''),
  });
  emit('select', person);
  emit('close');
};

const router = useRouter();

const goToSajuInput = () => {
  addContentLogAPI({ contentType: 'ap_harmony_btn_go_saju_input' });
  emit('close');
  router.push('/saju-input');
};

const getGenderText = (gender) => useFunc.getGenderTxt(gender);
const formatDate = (solar) => (solar ? useFunc.formatCustomDate(solar) : '-');

/** 선택 불가 사주 판별: 시간 모름 또는 내년 이후 */
const isDisabled = (person) => {
  if (person.birthTime === '25:00') return '시간 모름';
  if (person.solar) {
    const year = Number(person.solar.split('-')[0]);
    if (year > new Date().getFullYear()) return '내년 이후 사주';
  }
  return false;
};

const getTooltipText = (person) => {
  const reason = isDisabled(person);
  const isNaming = props.serviceType === 'naming';
  if (reason === '시간 모름') {
    return isNaming
      ? '🚫 태어난 시간이 없으면 정확한 작명이 어려워요.'
      : '🚫 태어난 시간이 없으면 정확한 궁합 분석이 어려워요.';
  }
  if (reason === '내년 이후 사주') {
    return isNaming
      ? '🚫 미래 사주는 작명이 어려워요.'
      : '🚫 미래 사주는 궁합 분석이 어려워요.';
  }
  return '';
};

const activeTooltipId = ref(null);
let tooltipTimer = null;

const showTooltip = (id, autoHide = false) => {
  clearTimeout(tooltipTimer);
  tooltipTimer = null;
  activeTooltipId.value = id;
  if (autoHide) {
    tooltipTimer = setTimeout(() => {
      activeTooltipId.value = null;
      tooltipTimer = null;
    }, 1000);
  }
};

const hideTooltip = () => {
  if (tooltipTimer) return;
  activeTooltipId.value = null;
};

const onItemPointerEnter = (e, person) => {
  if (!isDisabled(person)) return;
  if (e.pointerType === 'mouse') showTooltip(person.guestUserId, false);
};

const onItemPointerLeave = (e, person) => {
  if (!isDisabled(person)) return;
  if (e.pointerType === 'mouse') hideTooltip();
};

const onItemClick = (person) => {
  if (isDisabled(person)) {
    showTooltip(person.guestUserId, true);
    return;
  }
  selectPerson(person);
};

const onListScroll = () => {
  if (activeTooltipId.value === null) return;
  clearTimeout(tooltipTimer);
  tooltipTimer = null;
  activeTooltipId.value = null;
};

onBeforeUnmount(() => {
  clearTimeout(tooltipTimer);
});
</script>

<template>
  <Teleport to="body">
    <div v-if="isVisible" class="harmony-popup-overlay" @click.self="emit('close')" @touchmove.self.prevent>
      <div class="harmony-popup">
        <div class="harmony-popup__header">
          <span class="harmony-popup__title">저장목록에서 선택</span>
          <button class="harmony-popup__close" @click="emit('close')">✕</button>
        </div>

        <div class="harmony-popup__search">
          <input
            :value="searchKeyword"
            type="text"
            placeholder="이름, 생년월일(920101), 한글사주로 검색"
            class="harmony-popup__input"
            @input="onSearchInput"
          />
          <p class="harmony-popup__search-hint">
            이름, 생년월일(920101), 한글사주로 검색 가능합니다.
          </p>
        </div>

        <div
          ref="listRef"
          class="harmony-popup__list"
          :class="{ 'harmony-popup__list--loading': isPageLoading && !isLoading }"
          @scroll="onListScroll"
        >
          <div v-if="isLoading" class="harmony-popup__loading">불러오는 중...</div>
          <div v-else-if="!filteredList.length && !isPageLoading" class="harmony-popup__empty">
            <p>저장된 사주가 없습니다.</p>
            <button class="harmony-popup__empty-btn" @click="goToSajuInput">
              사주입력으로 가기
            </button>
          </div>
          <template v-else>
            <button
              v-for="person in filteredList"
              :key="person.guestUserId"
              class="harmony-popup__item"
              :class="{ 'harmony-popup__item--disabled': isDisabled(person) }"
              @click="onItemClick(person)"
              @pointerenter="onItemPointerEnter($event, person)"
              @pointerleave="onItemPointerLeave($event, person)"
            >
              <div class="harmony-popup__item-name">{{ person.guestUserName || '-' }}</div>
              <div class="harmony-popup__item-info">
                {{ getGenderText(person.gender) }} · {{ formatDate(person.solar) }}
              </div>
              <div v-if="isDisabled(person)" class="harmony-popup__item-badge">
                {{ isDisabled(person) }}
              </div>
              <div
                v-if="isDisabled(person) && activeTooltipId === person.guestUserId"
                class="harmony-popup__tooltip"
              >
                {{ getTooltipText(person) }}
              </div>
            </button>
          </template>
        </div>

        <div v-if="totalPages > 1 && !isLoading" class="harmony-popup__pagination">
          <button
            class="harmony-popup__page-btn"
            :disabled="currentPage === 1"
            @click="goToPage(currentPage - 1)"
          >
            ‹
          </button>
          <button
            v-for="page in visiblePages"
            :key="page"
            class="harmony-popup__page-btn"
            :class="{ 'harmony-popup__page-btn--active': page === currentPage }"
            @click="goToPage(page)"
          >
            {{ page }}
          </button>
          <button
            class="harmony-popup__page-btn"
            :disabled="currentPage === totalPages"
            @click="goToPage(currentPage + 1)"
          >
            ›
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style lang="scss" scoped>
.harmony-popup-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.harmony-popup {
  background: #fff;
  border-radius: 16px;
  width: 400px;
  max-width: 90vw;
  height: 70vh;
  max-height: 640px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.harmony-popup__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px 12px;
}

.harmony-popup__title {
  color: var(--Gray-800, #333);
  font-size: 18px;
  font-weight: 700;
  line-height: 28px;
}

.harmony-popup__close {
  width: 28px;
  height: 28px;
  border: none;
  background: transparent;
  color: var(--Gray-600, #757575);
  font-size: 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.harmony-popup__search {
  padding: 0 24px 12px;
}

.harmony-popup__search-hint {
  margin: 6px 2px 0;
  color: var(--Gray-600, #757575);
  font-size: 12px;
  line-height: 18px;
}

.harmony-popup__input {
  width: 100%;
  padding: 10px 16px;
  border-radius: 8px;
  border: 1px solid var(--Gray-300, #ebebeb);
  font-size: 14px;
  line-height: 20px;
  outline: none;
  box-sizing: border-box;

  &:focus {
    border-color: #e8818a;
  }
}

.harmony-popup__list {
  flex: 1;
  overflow-y: auto;
  padding: 0 24px;
  transition: opacity 0.15s ease;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
  scrollbar-gutter: stable both-edges;

  &--loading {
    opacity: 0.5;
    pointer-events: none;
  }
}

.harmony-popup__loading,
.harmony-popup__empty {
  text-align: center;
  padding: 40px 0;
  color: var(--Gray-600, #757575);
  font-size: 14px;
}

.harmony-popup__empty-btn {
  margin-top: 16px;
  padding: 10px 24px;
  border: none;
  border-radius: 8px;
  background: #e8818a;
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: #d6727b;
  }
}

.harmony-popup__item {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  width: 100%;
  padding: 14px 0;
  border: none;
  border-bottom: 1px solid var(--Gray-200, #f5f5f5);
  background: transparent;
  cursor: pointer;
  text-align: left;

  &:hover {
    background: rgba(232, 129, 138, 0.03);
  }

  &--disabled {
    cursor: not-allowed;

    .harmony-popup__item-name,
    .harmony-popup__item-info {
      opacity: 0.5;
    }

    &:hover {
      background: transparent;
    }
  }
}

.harmony-popup__tooltip {
  position: absolute;
  inset: 0;
  margin: auto;
  width: max-content;
  height: max-content;
  max-width: 240px;
  padding: 8px 15px;
  border-radius: 5px;
  background: var(--Gray-800, #333);
  color: #fff;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  text-align: left;
  white-space: normal;
  word-break: keep-all;
  z-index: 10;
  pointer-events: none;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.harmony-popup__item-badge {
  color: var(--Orange-400, #ff6b6b);
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
}

.harmony-popup__item-name {
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--Gray-800, #333);
  font-size: 15px;
  font-weight: 600;
  line-height: 22px;
}

.harmony-popup__item-info {
  color: var(--Gray-600, #757575);
  font-size: 13px;
  line-height: 20px;
}

.harmony-popup__pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 12px 24px 20px;
  border-top: 1px solid var(--Gray-200, #f5f5f5);
}

.harmony-popup__page-btn {
  min-width: 32px;
  height: 32px;
  padding: 0 6px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: var(--Gray-600, #757575);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover:not(:disabled) {
    background: var(--Gray-100, #fafafa);
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }

  &--active {
    background: #e8818a;
    color: #fff;

    &:hover:not(:disabled) {
      background: #d6727b;
      color: #fff;
    }
  }
}
</style>
